import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { clubInfo } from "@/lib/mbti";
import { Users, Wand2, Save, RotateCcw, Trophy, Eye, MonitorPlay } from "lucide-react";

const STATUS_LABEL = { competing: "Coopetir", observer: "Observador" };
const STATUS_COLOR = { competing: "#22C55E", observer: "#737373" };

function Poster({ data, statusOf, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.documentElement.requestFullscreen?.().catch(() => {});
    return () => {
      window.removeEventListener("keydown", onKey);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [onClose]);

  const total = data.clubs.reduce(
    (a, cl) => a + cl.candidates.filter((c) => statusOf(c) === "competing").length, 0);

  return createPortal((
    <div className="fixed inset-0 z-[100] bg-[#050505] overflow-y-auto p-10" data-testid="selection-poster">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="kw-overline mb-2">Growth League · 1ª Edição · arranque 12 de outubro</div>
          <h1 className="font-display text-6xl font-black uppercase tracking-tighter">
            Os <span className="text-[#E50000]">{total}</span> que vão coopetir
          </h1>
        </div>
        <button onClick={onClose} className="kw-btn-secondary" data-testid="selection-poster-close">Sair (Esc)</button>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-6">
        {data.clubs.map((cl) => {
          const info = clubInfo(cl.club);
          const comp = cl.candidates.filter((c) => statusOf(c) === "competing");
          return (
            <div key={cl.club} className="border p-5" style={{ borderColor: info?.border, background: `linear-gradient(180deg, ${info?.bg} 0%, #0A0A0A 60%)` }}>
              <h2 className="font-display text-3xl font-black uppercase mb-1" style={{ color: info?.color }}>{cl.club}</h2>
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-4">
                {comp.length}/{data.quota_active} concorrentes
              </div>
              <ol className="space-y-1.5">
                {comp.map((c, i) => (
                  <li key={c.id} className="flex items-baseline gap-3">
                    <span className="font-mono text-xs text-[#555] w-4">{i + 1}</span>
                    <span className="font-display text-lg font-black uppercase tracking-tight leading-tight">{c.name}</span>
                  </li>
                ))}
                {comp.length === 0 && <li className="text-sm text-[#555]">Por escolher</li>}
              </ol>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] uppercase tracking-widest text-[#444] mt-8">
        Coopetimos para crescer · não para ganhar a qualquer custo
      </p>
    </div>
  ), document.body);
}

export default function Selection() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});           // player_id -> status
  const [saving, setSaving] = useState(false);
  const [onlyApplicants, setOnlyApplicants] = useState(false);
  const [poster, setPoster] = useState(false);
  const [apps, setApps] = useState(null);

  const load = () => {
    api.get("/league/selection").then((r) => { setData(r.data); setDraft({}); })
      .catch((err) => toast.error(formatApiError(err)));
    api.get("/applications/settings").then((r) => setApps(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const statusOf = (c) => draft[c.id] ?? c.league_status;

  const counts = useMemo(() => {
    if (!data) return {};
    const out = {};
    data.clubs.forEach((cl) => {
      out[cl.club] = { competing: 0 };
      cl.candidates.forEach((c) => {
        if (statusOf(c) === "competing") out[cl.club].competing += 1;
      });
    });
    return out;
  }, [data, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = (club, c, status) => {
    const cur = counts[club] || { competing: 0 };
    if (status === "competing" && statusOf(c) !== "competing" && cur.competing >= data.quota_active) {
      toast.error(`${club} já tem ${data.quota_active} a coopetir`); return;
    }
    setDraft((d) => ({ ...d, [c.id]: status }));
  };

  const suggest = (cl) => {
    const pool = cl.candidates.filter((c) => !c.expelled);
    const next = { ...draft };
    pool.forEach((c, i) => {
      next[c.id] = i < data.quota_active ? "competing" : "observer";
    });
    setDraft(next);
    toast.success(`${cl.club}: ${Math.min(pool.length, data.quota_active)} concorrentes por ordem de resposta ao MBTI`);
  };

  const save = async () => {
    const assignments = Object.entries(draft).map(([player_id, league_status]) => ({ player_id, league_status }));
    if (assignments.length === 0) { toast.error("Nada para gravar"); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post("/league/selection", { assignments });
      toast.success(`Seleção gravada — ${res.changed} alterações. A coopetição conta a partir da semana ${res.competition_start_week}.`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  if (!data) return <div className="kw-overline">A carregar…</div>;

  const totalCompeting = Object.values(counts).reduce((a, b) => a + b.competing, 0);
  const dirty = Object.keys(draft).length;

  return (
    <div className="space-y-8 kw-fade-up" data-testid="selection-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="kw-overline mb-2">Reunião de seleção · 8 de outubro</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Seleção dos <span className="text-[#E50000]">32</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            {data.quota_active} concorrentes por clube. Quem não for escolhido
            continua Observador, com o seu Ranking Accountability. A coopetição conta a partir da
            semana {data.competition_start_week} ({data.competition_start_iso}).
          </p>
          {apps?.deadline_iso && (
            <p className="text-[11px] uppercase tracking-widest text-[#EAB308] mt-3" data-testid="selection-deadline-note">
              Candidaturas fecham {new Date(apps.deadline_iso + "T12:00:00").toLocaleDateString("pt-PT", { weekday: "long", day: "2-digit", month: "long" })} às 24:00 —
              fecha a seleção e o cartaz só depois disso
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button className="kw-btn-secondary inline-flex items-center gap-2" onClick={() => setPoster(true)}
            data-testid="selection-poster-open">
            <MonitorPlay className="w-4 h-4" /> Cartaz da reunião
          </button>
          {dirty > 0 && (
            <button className="kw-btn-secondary inline-flex items-center gap-2" onClick={() => setDraft({})}
              data-testid="selection-reset">
              <RotateCcw className="w-4 h-4" /> Descartar ({dirty})
            </button>
          )}
          <button className="kw-btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving || !dirty}
            data-testid="selection-save">
            <Save className="w-4 h-4" /> {saving ? "A gravar…" : "Gravar seleção"}
          </button>
        </div>
      </div>

      {poster && <Poster data={data} statusOf={statusOf} onClose={() => setPoster(false)} />}

      <div className="grid grid-cols-3 gap-4" data-testid="selection-totals">
        {[
          { icon: Trophy, label: "A coopetir", value: `${totalCompeting}/${data.quota_active * 4}`, color: "#22C55E" },
          { icon: Users, label: "Candidatos elegíveis", value: data.clubs.reduce((a, c) => a + c.total, 0), color: "#fff" },
          { icon: Eye, label: "Sem clube (falta MBTI)", value: data.players_without_club, color: "#EAB308" },
        ].map((k) => (
          <div key={k.label} className="kw-card p-5">
            <div className="flex items-center justify-between mb-3">
              <k.icon className="w-5 h-5" style={{ color: k.color }} />
              <div className="kw-overline">{k.label}</div>
            </div>
            <div className="font-display text-3xl font-black leading-none" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#A3A3A3] cursor-pointer">
        <input type="checkbox" checked={onlyApplicants} onChange={(e) => setOnlyApplicants(e.target.checked)}
          data-testid="selection-only-applicants" />
        Mostrar só quem se candidatou
      </label>

      <div className="grid xl:grid-cols-2 gap-6">
        {data.clubs.map((cl) => {
          const info = clubInfo(cl.club);
          const c = counts[cl.club] || { competing: 0 };
          const rows = onlyApplicants ? cl.candidates.filter((x) => x.application_status) : cl.candidates;
          return (
            <div key={cl.club} className="kw-card overflow-hidden" data-testid={`selection-club-${cl.club}`}>
              <div className="p-5 border-b border-[#222] flex items-center justify-between gap-3 flex-wrap"
                style={{ background: `linear-gradient(90deg, ${info?.bg} 0%, transparent 80%)` }}>
                <div>
                  <h2 className="font-display text-2xl font-black uppercase" style={{ color: info?.color }}>{cl.club}</h2>
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-1">
                    <span style={{ color: c.competing === data.quota_active ? "#22C55E" : "#EAB308" }}>
                      {c.competing}/{data.quota_active} a coopetir
                    </span>
                    {" · "}{rows.length} candidatos
                  </div>
                </div>
                <button className="kw-btn-secondary !py-2 !px-4 text-[10px] inline-flex items-center gap-2"
                  onClick={() => suggest(cl)} data-testid={`selection-suggest-${cl.club}`}>
                  <Wand2 className="w-3.5 h-3.5" /> Sugerir
                </button>
              </div>

              <div className="max-h-[560px] overflow-y-auto divide-y divide-[#1A1A1A]">
                {rows.length === 0 && (
                  <div className="p-5 text-sm text-[#737373]">Sem candidatos com este filtro.</div>
                )}
                {rows.map((cand) => {
                  const st = statusOf(cand);
                  const pending = draft[cand.id] && draft[cand.id] !== cand.league_status;
                  return (
                    <div key={cand.id} className={`flex items-center gap-3 px-4 py-2.5 ${pending ? "bg-[#141414]" : ""}`}
                      data-testid={`selection-row-${cand.id}`}>
                      <div className="w-7 text-center font-mono text-xs text-[#555]">{cand.queue_position}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {cand.name}
                          {cand.expelled && <span className="ml-2 text-[9px] uppercase text-[#E50000]">expulso</span>}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                          {cand.mbti || "—"}
                          {cand.mbti_answered_at ? ` · MBTI ${String(cand.mbti_answered_at).slice(0, 10)}` : " · sem hora MBTI"}
                          {cand.application_status ? ` · candidatura ${cand.application_status}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {["competing", "observer"].map((s) => (
                          <button key={s} onClick={() => setStatus(cl.club, cand, s)}
                            className="text-[9px] font-bold uppercase tracking-widest px-2 py-1.5 border transition-colors"
                            style={st === s
                              ? { borderColor: STATUS_COLOR[s], color: STATUS_COLOR[s], background: `${STATUS_COLOR[s]}1A` }
                              : { borderColor: "#262626", color: "#737373" }}
                            data-testid={`selection-${s}-${cand.id}`}>
                            {STATUS_LABEL[s]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
